import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subscription,
  catchError,
  map,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { ServiceConstant } from '../utils/service-constant.util';
import { FNFResponse } from '../models/response';
import {
  InboxPage,
  MessageCounts,
  NotificationPreferences,
  NotificationPreferencesPatch,
} from '../models/notification';

/**
 * `/v1/notifications/apps/*` through the gateway — the notification service's
 * per-user inbox and the per-user notification preferences (FNF-415).
 *
 * The gateway reads the user and their role from the bearer token, so no id
 * travels in the path; a manager and an admin asking for the same URL get
 * different cards back.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly http = inject(HttpClient);
  private static readonly base = `${ServiceConstant.apiUrl}/notifications`;

  /**
   * Unread inbox count — the number on the header bell. Kept here so the bell
   * and the inbox page share one source; the inbox pushes to it after marking
   * rows read rather than each re-fetching.
   */
  readonly unreadCount$ = new BehaviorSubject<number>(0);

  /**
   * How often the badge asks for the count while the dashboard is open. The
   * web app has no push channel of its own, so a new notification would
   * otherwise stay invisible until the next full page load.
   */
  static readonly pollMs = 60_000;

  private poll?: Subscription;

  /**
   * Starts the badge poll. Idempotent, and quiet while the tab is in the
   * background — a dashboard left open on a second monitor should not keep
   * asking all day.
   */
  startPolling(): void {
    if (this.poll) return;
    this.poll = timer(0, NotificationService.pollMs)
      .pipe(
        // `visibilityState` is undefined in unit tests' jsdom-less runs.
        switchMap(() =>
          typeof document !== 'undefined' && document.visibilityState === 'hidden'
            ? EMPTY
            : // One failed tick must not end the poll.
              this.getCounts().pipe(catchError(() => EMPTY)),
        ),
      )
      .subscribe({ error: () => undefined });
  }

  stopPolling(): void {
    this.poll?.unsubscribe();
    this.poll = undefined;
  }

  /** One page of the inbox, newest first. `cursor` is the previous page's key. */
  getMessages(limit = 20, cursor?: string | null): Observable<InboxPage> {
    let params = new HttpParams().set('limit', limit);
    if (cursor) {
      params = params.set('last_evaluated_key', cursor);
    }
    return this.http
      .get<FNFResponse<InboxPage>>(`${NotificationService.base}/apps/messages`, { params })
      .pipe(map((res) => res.data ?? emptyPage()));
  }

  /** Totals for the badge. Cheaper than a page of rows nobody has opened. */
  getCounts(): Observable<MessageCounts> {
    return this.http
      .get<FNFResponse<MessageCounts>>(`${NotificationService.base}/apps/messages-counts`)
      .pipe(
        map((res) => res.data ?? { total_count: 0, unread_count: 0 }),
        tap((counts) => this.unreadCount$.next(counts.unread_count)),
      );
  }

  /** Refreshes the badge without a caller having to care about the result. */
  refreshCounts(): void {
    this.getCounts().subscribe({ error: () => undefined });
  }

  markRead(notificationId: string): Observable<unknown> {
    return this.http.patch(`${NotificationService.base}/apps/messages/${notificationId}/read`, {});
  }

  markUnread(notificationId: string): Observable<unknown> {
    return this.http.patch(
      `${NotificationService.base}/apps/messages/${notificationId}/unread`,
      {},
    );
  }

  /** Every unread row at once — the inbox's "Mark all as read". */
  markAllRead(): Observable<unknown> {
    return this.http.patch(`${NotificationService.base}/apps/messages/read-all`, {});
  }

  /**
   * The signed-in user's preferences. The service materialises a document
   * from the role's schema on first read, so this never 404s.
   */
  getPreferences(): Observable<NotificationPreferences> {
    return this.http
      .get<FNFResponse<NotificationPreferences>>(`${NotificationService.base}/apps/preferences`)
      .pipe(map((res) => res.data as NotificationPreferences));
  }

  /**
   * Partial update — send only what changed. Answers with the whole document,
   * so the caller replaces its copy rather than merging.
   *
   * 400 carries `category_locked`, `channel_not_allowed` or
   * `category_not_available` in the error code; 409 means another client wrote
   * first (re-read, then retry).
   */
  updatePreferences(patch: NotificationPreferencesPatch): Observable<NotificationPreferences> {
    return this.http
      .patch<FNFResponse<NotificationPreferences>>(
        `${NotificationService.base}/apps/preferences`,
        patch,
      )
      .pipe(map((res) => res.data as NotificationPreferences));
  }
}

function emptyPage(): InboxPage {
  return { items: [], last_evaluated_key: null, total_count: 0, unread_count: 0 };
}
